import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Divider,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import bandpic from "../resources/EDUVIC B2C Hero3-coaching.png";

const FeesPage = () => {
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const platformBg = useColorModeValue("gray.50", "gray.700");

  return (
    <PublicLayout>

      {/* Contact Hero Section */}
      <Box
        as="section"
        minH={{ base: "260px", md: "320px" }}
        bgImage={bandpic}
        bgSize="cover"
        bgPosition="right center"
        bgRepeat="no-repeat"
        position="relative"
        overflow="hidden"
      >
        {/* LEFT Gradient Overlay for readability */}
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          bgGradient="
            linear(
              to-r,
              rgba(0,0,0,0.68),
              rgba(0,0,0,0.35),
              rgba(0,0,0,0)
            )
          "
          zIndex={1}
        />

        {/* Content */}
        <Box
          position="relative"
          zIndex={2}
          minH={{ base: "260px", md: "320px" }}
          display="flex"
          alignItems="center"
          px={{ base: 6, md: 16 }}
        >
          <VStack
            align="flex-start"
            spacing={4}
            maxW="700px"
          >

        {/* Headline */}
        <Heading
          color="white"
          size="2xl"
          lineHeight="1.2"
        >
          Coaching Fees & Enrolment
        </Heading>

        {/* Subtext */}
        <Text
          color="gray.100"
          fontSize={{ base: "md", md: "lg" }}
          lineHeight="1.7"
          maxW="650px"
        >
          Understand the fees, payment options and enrolment terms for Eduvic Guided Preparation.
        </Text>

          </VStack>
        </Box>
      </Box>


      <Box
        maxW="1200px"
        mx="auto"
        px={{ base: 6, md: 8 }}
        py={{ base: 10, md: 14 }}
      >

        {/* INTRODUCTION */}

        <VStack
          spacing={4}
          textAlign="center"
          mb={10}
        >
          <Heading
            size={{ base: "lg", md: "xl" }}
          >
            Guided Preparation Fees
          </Heading>

          <Text
            color={mutedText}
            fontSize={{ base: "md", md: "lg" }}
            lineHeight="1.8"
            maxW="800px"
          >
            Eduvic Coaching provides structured examination preparation
            through scheduled online classes, regular assessment and
            supporting digital learning resources.
          </Text>

          <Text
            color={mutedText}
            fontSize="md"
            lineHeight="1.8"
            maxW="800px"
          >
            Relevant Eduvic Practice Platform resources are also provided
            as complimentary access where they form part of the selected
            Guided Preparation pathway.
          </Text>
        </VStack>


{/* FEES */}

<Box
  bg={platformBg}
  borderRadius="2xl"
  p={{ base: 6, md: 8 }}
  mb={8}
>

  <VStack
    align="stretch"
    spacing={6}
  >

    <Box>
      <Heading size="md" mb={3}>
        Guided Preparation — Term Fee
      </Heading>

      <Text
        color={mutedText}
        lineHeight="1.8"
        maxW="850px"
      >
        Guided Preparation is offered on a term basis. Each term provides
        a defined amount of guided teaching, with the pace and emphasis
        adjusted according to the student's preparation level, progress
        and examination timeline.
      </Text>
    </Box>


    {/* FEE HIGHLIGHT */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={{ base: 6, md: 8 }}
    >

      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        spacing={6}
        alignItems="center"
      >

        <Box>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={mutedText}
            mb={1}
          >
            TERM FEE
          </Text>

          <Text
            fontSize="3xl"
            fontWeight="bold"
          >
            $750
          </Text>

          <Text
            fontSize="sm"
            color={mutedText}
            mt={1}
          >
            per student, per term
          </Text>
        </Box>


        <Box>
          <Text
            fontWeight="semibold"
            mb={2}
          >
            20 Guided Classes
          </Text>

          <Text
            color={mutedText}
            lineHeight="1.7"
          >
            10 English classes + 10 Mathematics classes,
            with each scheduled class running for 1 hour.
          </Text>
        </Box>


        <Box>
          <Text
            fontWeight="semibold"
            mb={2}
          >
            Included in the Term
          </Text>

          <Text
            color={mutedText}
            lineHeight="1.7"
          >
            End-of-term online examination through the Eduvic Learning
            Platform and complimentary access to the relevant Eduvic
            Practice Platform offering.
          </Text>
        </Box>

      </SimpleGrid>

    </Box>


    {/* WHAT THIS MEANS */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={{ base: 6, md: 7 }}
    >

      <Heading size="sm" mb={3}>
        Preparation Pace is Adjusted to the Student
      </Heading>

      <Text
        color={mutedText}
        lineHeight="1.8"
      >
        Students do not have to commit to a fixed four-term or two-term
        course structure. Guided Preparation can be continued across
        multiple terms, while the teaching pace within each term can be
        adjusted according to the student's existing knowledge, progress
        and examination timeline. Where more intensive preparation is
        required, lessons can move more quickly; where additional
        foundation work or consolidation is needed, more time can be
        allocated to those areas.
      </Text>

    </Box>


    {/* TRANSPARENCY NOTE */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={{ base: 6, md: 7 }}
    >

      <Heading size="sm" mb={3}>
        What the Term Fee Represents
      </Heading>

      <Text
        color={mutedText}
        lineHeight="1.8"
      >
        The term fee is based on the scheduled guided teaching and
        supporting preparation provided during that term. The number of
        terms a student may require will depend on when they begin,
        their existing level of preparation and the pace at which they
        progress towards the examination.
      </Text>

    </Box>

  </VStack>

</Box>


        {/* WHAT THE FEE INCLUDES */}

        <Box mb={10}>

          <Heading size="md" mb={5}>
            What is Included
          </Heading>

          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={4}
          >

            {[
              "Scheduled online Guided Preparation classes",
              "Guided teaching and problem-solving support",
              "Digital lessons and supporting learning material",
              "Scheduled assessments and examinations",
              "Progress and performance information",
              "Complimentary access to relevant Eduvic Practice resources",
            ].map((item) => (
              <Box
                key={item}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
              >
                <Text color={mutedText}>
                  • {item}
                </Text>
              </Box>
            ))}

          </SimpleGrid>

        </Box>


        {/* FEE TERMS */}

        <Box mb={10}>

          <Heading size="md" mb={5}>
            Fee & Enrolment Terms
          </Heading>

          <VStack
            align="start"
            spacing={3}
            color={mutedText}
            lineHeight="1.8"
          >
            <Text>
              • Fees are based on the selected Guided Preparation
              pathway and scheduled program.
            </Text>

            <Text>
              • The applicable fee and program details will be confirmed
              before enrolment.
            </Text>

            <Text>
              • Where a program commences after the scheduled start date,
              applicable fees may be adjusted according to the remaining
              program.
            </Text>

            <Text>
              • Relevant Eduvic Practice Platform resources are provided
              as complimentary access where included in the selected
              preparation pathway.
            </Text>

            <Text>
              • Program availability, class schedules and enrolment
              arrangements may vary by examination pathway.
            </Text>
          </VStack>

        </Box>


        <Divider mb={10} />


        {/* CTA */}

        <VStack
          spacing={4}
          textAlign="center"
        >

          <Heading size="md">
            Ready to discuss Guided Preparation?
          </Heading>

          <Text
            color={mutedText}
            maxW="700px"
            lineHeight="1.8"
          >
            Review the preparation pathway for your examination and
            contact Eduvic to confirm the available program, schedule
            and enrolment options.
          </Text>

          <HStack
            spacing={4}
            flexWrap="wrap"
            justify="center"
          >
            <Button
              colorScheme="blue"
              onClick={() => navigate("/contact")}
            >
              Enquire About Guided Preparation
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate("/enrol")}
            >
              Start Enrolment
            </Button>
          </HStack>

        </VStack>

      </Box>

    </PublicLayout>
  );
};

export default FeesPage;